import { useEffect, useContext, useMemo, createContext, useState, useRef } from 'react';
import { useSecurity } from '../Security/security';
import { useMsal } from '@azure/msal-react';
import { verifySSOToken } from '../../api/authentication';
import { StoreContext } from '../Store';
import { useNavigate, useLocation } from 'react-router-dom';
import { blankRedirectUrl } from '../../utils/msal-config.js';
import { InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import { processSSOResponse, SSOLoginPopupWrapper } from '../../utils/sso';
import { SSOModal } from './SSOModal';

export const SSOContext = createContext({});

export function SSOProvider({ children }) {
  const store = useContext(StoreContext);
  const { instance } = useMsal();
  const { dispatch } = useSecurity();
  const navigate = useNavigate();
  const location = useLocation();

  const [triggerLogin, setTriggerLogin] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [showPopupBlockerModal, setShowPopupBlockerModal] = useState(false);
  const authAttempted = useRef(false);

  const setIsLoadingSSO = store.isLoadingSSO[1];

  useEffect(() => {
    const initializeMsal = async () => {
      if (authAttempted.current) {
        return;
      }

      authAttempted.current = true;

      await instance.initialize();
      const request = {
        scopes: ['profile'],
        redirectUri: blankRedirectUrl,
      };

      try {
        setIsLoadingSSO(true);
        const response = await instance.ssoSilent(request);

        if (response.accessToken) {
          if (response.account) {
            store.ssoUserDetails[1]({
              name: response.account.name,
              username: response.account.username,
            });
          }
          processSSOResponse(response, store, dispatch, setRedirecting, navigate);
          setIsLoadingSSO(false);
        }
      } catch (e) {
        //check if the user is coming from the SSO website or if they're already logged in as an SSO user. 
        //we'll show the pop up only if these conditions are true.
        const urlParams = new URLSearchParams(location.search);
        const ssoredirect = urlParams.get('ssoredirect') || false;

        const isSSOUser = ssoredirect == 1 || sessionStorage.getItem('sso') === '1'

        if (e instanceof InteractionRequiredAuthError && e.errorMessage.includes('cookies') && isSSOUser) {
          const response = await SSOLoginPopupWrapper(instance, request);
          if (response.error) {
            if (response.errorCode === 'popup_window_error') {
              setShowPopupBlockerModal(true);
            }
            return;
          }

          if (response?.accessToken) {
            processSSOResponse(response, store, dispatch, setRedirecting, navigate);
            setIsLoadingSSO(false);
            authAttempted.current = false;
            return;
          }
        } else if (e instanceof BrowserAuthError) {
          // Specifically handle interaction_in_progress error
          if (e.errorCode === 'interaction_in_progress') {
            setIsLoadingSSO(false);
            authAttempted.current = false;
            return;
          }

          authAttempted.current = false;
          return;
        }
        setIsLoadingSSO(false);

        if (['consent_required'].includes(e.errorCode)) {
          // usually if user has not given persmissions to GB ENS application
          instance.acquireTokenRedirect(request).catch((error) => {
            console.log(error);
          });
        } else {
          // if not signed in with SSO, then make sure we clear sessions storage .etc
          await instance.logoutRedirect({
            onRedirectNavigate: (url) => {
              return false;
            },
          });

          if (sessionStorage.getItem('sso') === '1') {
            sessionStorage.setItem('sso', 0);
            dispatch({ type: 'logout' });
          }
        }
      } finally {
        setIsLoadingSSO(false);
      }
    };

    if (triggerLogin) {
      authAttempted.current = false;
      initializeMsal();
      setTriggerLogin(false);
    }
  }, [triggerLogin]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const ssoredirect = urlParams.get('ssoredirect') || false;

    if (ssoredirect && redirecting) {
      ssoredirect == 1 && navigate('/dashboard');
    }
  }, [redirecting]);

  const handleSSOLogout = async () => {
    store.ssoUserDetails[1]({});
    store.isSSOUserWithNoCompany[1](false);
    sessionStorage.setItem('sso', 0);
    await instance.logoutRedirect({});
  };

  const context = useMemo(
    () => ({
      handleSSOLogout,
      setTriggerLogin,
    }),
    [handleSSOLogout, setTriggerLogin]
  );

  return (
    <SSOContext.Provider value={context}>
      {children}
      <SSOModal showPopupBlockerModal={showPopupBlockerModal} setShowPopupBlockerModal={setShowPopupBlockerModal} />
    </SSOContext.Provider>
  );
}

export const useSSO = () => {
  return useContext(SSOContext);
};
